import React from "react";

import {
  getRoutePath,
  URLFormat,
  ClientRouteName,
} from "@every.org/common/src/helpers/clientRoutes";

import { FeesTable } from "src/components/Legal/FeesTable";
import { Link } from "src/components/Link";
import { ArticlePageContent } from "src/components/layout/ArticlePage";
import { DefaultPageLayout } from "src/components/layout/DefaultPageLayout";
import { HeaderTitlePageSection } from "src/components/layout/PageSection";
import {
  useScrollToAnchor,
  SectionAnchor,
} from "src/pages/Legal/TermsOfService";
import { LinkAppearance } from "src/styles/link";

const LAST_UPDATED_STRING = "09/28/2023";

const SECTION_HEADERS: { [key: string]: string } = {
  fees: "Third Party Fees",
  timing: "Timing",
};

const Disbursements: React.FCC = () => {
  useScrollToAnchor();
  return (
    <DefaultPageLayout
      pageTitle="How Donation Disbursements and Third-Party Fees Work | Every.org"
      metas={{
        "og:description":
          "There are no hidden fees on Every.org. Get transparent information about third-party transaction fees and disbursement options for your nonprofit.",
      }}
      headerContent={
        <HeaderTitlePageSection>
          <h1>Disbursements</h1>
        </HeaderTitlePageSection>
      }
    >
      <ArticlePageContent>
        <p>
          This page provides an overview of the third party fees and timing for
          donation payments and disbursements to nonprofits. Please see our{" "}
          <Link
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.TERMS_OF_SERVICE,
            })}
            data-tname="Terms"
            target="terms"
          >
            Terms of Service
          </Link>{" "}
          for full information regarding disbursements and if you have any
          questions, check out our{" "}
          <Link
            appearance={LinkAppearance.HYPERLINK}
            data-tname="support"
            target="support"
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.SUPPORT,
            })}
          >
            frequently asked questions
          </Link>
          .
        </p>
        <SectionAnchor id="fees" />
        <h2>{SECTION_HEADERS["fees"]}</h2>
        <p>
          Every.org does not charge any of our own fees. Instead, after choosing
          how much to donate, people can add an additional donation supporting
          Every.org and our mission to build accessible giving infrastructure.
          These tips are completely optional. Below are the third party payment
          transaction costs charged by our partners as of {LAST_UPDATED_STRING}.
          Every.org currently covers all transaction costs on donations made
          with bank accounts. Please note that these may change at any time.
        </p>
        <FeesTable />
        <p>
          Some nonprofits receive funds through Network for Good which charges a
          disbursement fee of 2.25% (though currently Every.org covers this fee
          for all donations made with a bank account in the online donation
          flow). We recommend all nonprofits{" "}
          <Link
            data-tname="addBankInfo"
            to="https://support.every.org/hc/en-us/articles/360061887233"
            appearance={LinkAppearance.HYPERLINK}
            target="_blank"
          >
            add their bank information
          </Link>{" "}
          directly or join PayPal Grants to receive funds with zero disbursement
          fee. Both these options avoid the Network for Good fee, leaving only
          the above fees.
        </p>
        <SectionAnchor id="timing" />
        <h2>{SECTION_HEADERS["timing"]}</h2>
        <p>
          After a nonprofit{" "}
          <Link
            data-tname="claimProfile"
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.NONPROFITS_ONBOARDING_SEARCH,
            })}
            appearance={LinkAppearance.HYPERLINK}
          >
            claims their profile
          </Link>
          , they will see information about donations in their Admin Dashboard
          immediately. If a nonprofit adds bank information, funds will be
          disbursed weekly as a direct deposit to the organization&rsquo;s bank
          account by default, or earlier if requested by the nonprofit.
        </p>
        <p>
          If a nonprofit has not connected a bank account, we will attempt to
          disburse funds via PayPal Grants. If an organization is eligible for
          PayPal Grants because they have{" "}
          <Link
            data-tname="paypalGetStarted"
            to="https://www.paypal.com/us/non-profit/getting-started-fundraise"
            appearance={LinkAppearance.HYPERLINK}
            target="_blank"
          >
            registered with PayPal as a valid nonprofit
          </Link>
          , we will disburse their first donation within 1 month. Afterward, we
          encourage them to add direct deposit information for weekly
          disbursements. If an organization chooses not to provide direct
          deposit information, funds will continue to be disbursed monthly. This
          allows nonprofits to choose the method that works best for them while
          minimizing the disbursement fees covered by Every.org.
        </p>
        <p>
          Finally, if we cannot disburse via direct deposit or PayPal Grants, we
          aggregate donations from the previous month on the 1st of each month
          and send them to our disbursement partner, Network for Good (NFG), who
          sends the payments{" "}
          <Link
            data-tname="nfgExplanation"
            to="https://intercom.help/networkforgood/en/articles/10059920-how-do-i-know-when-i-will-receive-my-disbursement"
            appearance={LinkAppearance.HYPERLINK}
            target="_blank"
          >
            around the 15th of the month
          </Link>
          . The donation will reach nonprofits faster if they have{" "}
          <Link
            data-tname="nfgDirectDeposit"
            to="https://intercom.help/networkforgood/en/articles/9788366-how-do-i-apply-for-direct-deposit"
            appearance={LinkAppearance.HYPERLINK}
            target="_blank"
          >
            direct deposit set up with NFG
          </Link>
          , otherwise NFG sends checks in the mail.
        </p>
        <p>
          The minimum for a disbursement through NFG is $10 USD. If we have less
          than the minimum for a nonprofit, Every.org will wait until other
          donations are made to meet the minimum disbursement amount before
          disbursing the funds.
        </p>
      </ArticlePageContent>
    </DefaultPageLayout>
  );
};

// Use default export to support React Lazy Loading.
export default Disbursements;
